import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import useCreateVoucher from "api/VoucherResource/useCreateVoucher";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoIosArrowForward } from "react-icons/io";
import { useMemo, useState } from "react";
import Dialog from "components/Overlays/ResponsiveDialog";
import { BiChevronRight, BiPlus } from "react-icons/bi";
import VoucherEntryFields from "./VoucherEntryFields";
import {
	createVoucherEntry,
	MAX_TOTAL_VOUCHERS,
	MAX_VOUCHER_ENTRIES,
	MAX_VOUCHER_QUANTITY,
	totalVoucherCount,
	toVoucherPayload,
} from "./voucherForm";

const validationSchema = Yup.object({
	vouchers: Yup.array()
		.of(
			Yup.object({
				number_of_tokens: Yup.number()
					.typeError("Enter number of tokens")
					.required("Enter number of tokens")
					.min(1, "Number of tokens must be greater than 0"),
				expiry_date: Yup.string().required("Expiry date is required"),
				is_hlf: Yup.boolean(),
				tag: Yup.string().max(200, "Label must be 200 characters or less"),
				currency: Yup.string().required("Select a currency"),
				quantity: Yup.number()
					.typeError("Enter a quantity")
					.required("Enter a quantity")
					.integer("Quantity must be a whole number")
					.min(1, "Quantity must be at least 1")
					.max(
						MAX_VOUCHER_QUANTITY,
						`Quantity cannot exceed ${MAX_VOUCHER_QUANTITY}`
					),
			})
		)
		.min(1, "Add at least one voucher")
		.max(
			MAX_VOUCHER_ENTRIES,
			`You can add at most ${MAX_VOUCHER_ENTRIES} voucher entries`
		)
		.test(
			"max-total",
			`You can create at most ${MAX_TOTAL_VOUCHERS} vouchers at once`,
			(entries) => totalVoucherCount(entries) <= MAX_TOTAL_VOUCHERS
		),
});

const CreateVoucher = () => {
	const today = new Date().toISOString().split("T")[0];
	const [visible, setVisible] = useState(false);

	const initialValues = useMemo(
		() => ({
			vouchers: [createVoucherEntry()],
		}),
		[]
	);

	const { mutateAsync, isLoading } = useCreateVoucher();

	const handleSubmit = async (data, { resetForm }) => {
		try {
			await mutateAsync({ vouchers: toVoucherPayload(data.vouchers) });
			resetForm({ values: { vouchers: [createVoucherEntry()] } });
			setVisible(false);
		} catch {
			// Error toast is handled by the mutation.
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setVisible(true)}
				className="portal-button portal-button-primary"
			>
				Create vouchers
				<BiChevronRight size={25} />
			</button>

			<Dialog
				visible={visible}
				onHide={() => setVisible(false)}
				position="top"
				modal
				draggable={false}
				style={{ width: "90vw", maxWidth: "1120px" }}
				contentStyle={{ overflowY: "auto", maxHeight: "70vh" }}
				contentClassName="rounded-b-xl"
				className="rounded-xl relative"
				header={
					<div className="text-xl font-sans font-bold sticky top-4 z-20 bg-white mr-2">
						<p>Create vouchers</p>
					</div>
				}
			>
				<p className="text-sm text-gray-600">
					Add a row for each voucher type. Qty is how many codes to generate
					with those details. Create saves every row. Required fields are marked
					with a star (*).
				</p>

				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ values, errors, submitCount }) => {
						const voucherCount = totalVoucherCount(values.vouchers);
						const canAddEntry =
							values.vouchers.length < MAX_VOUCHER_ENTRIES &&
							voucherCount < MAX_TOTAL_VOUCHERS;
						const createLabel =
							voucherCount === 1
								? "Create voucher"
								: `Create ${voucherCount || 0} vouchers`;

						return (
							<Form className="mt-4 space-y-5">
								<FieldArray name="vouchers">
									{({ push, remove }) => (
										<div className="space-y-3">
											<div className="overflow-x-auto">
												<div className="divide-y divide-gray-200">
													{values.vouchers.map((voucher, index) => (
														<VoucherEntryFields
															key={voucher.key}
															index={index}
															today={today}
															canRemove={values.vouchers.length > 1}
															onRemove={() => remove(index)}
														/>
													))}
												</div>
											</div>

											<button
												type="button"
												onClick={() =>
													push(
														createVoucherEntry(
															values.vouchers[values.vouchers.length - 1]
														)
													)
												}
												disabled={!canAddEntry}
												className="rounded-lg border-2 border-dashed border-akgreen px-4 py-2 text-akgreenest inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
											>
												<BiPlus size={20} aria-hidden="true" />
												Add another voucher
											</button>
										</div>
									)}
								</FieldArray>

								{typeof errors.vouchers === "string" && submitCount > 0 && (
									<p className="text-yellow-500 text-xs pl-2" role="alert">
										{errors.vouchers}
									</p>
								)}

								<div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-300 pt-4">
									<p className="text-sm text-gray-600">
										{voucherCount === 1
											? "1 voucher will be created."
											: `${voucherCount || 0} vouchers will be created.`}
									</p>
									<button
										type="submit"
										disabled={isLoading || voucherCount < 1}
										className="px-4 py-2 rounded-full bg-akgreenest text-white flex items-center gap-1 disabled:opacity-60"
									>
										{isLoading ? (
											<>
												Creating{" "}
												<AiOutlineLoading3Quarters
													size={20}
													className="animate-spin"
												/>
											</>
										) : (
											<>
												{createLabel} <IoIosArrowForward size={20} />
											</>
										)}
									</button>
								</div>
							</Form>
						);
					}}
				</Formik>
			</Dialog>
		</>
	);
};

export default CreateVoucher;
